import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, OnInit
} from '@angular/core';
import {BaseComponent} from "../base-component";
import {buffer, filter, sampleTime, takeUntil} from "rxjs/operators";
import {NotificationMessage} from "../models/notification-message";
import {MesNotificationService} from "../mes-notification.service";
import {Page} from "../models/filter.model";
import {InlineSVGModule} from "ng-inline-svg-2";
import {NgbDropdownModule} from "@ng-bootstrap/ng-bootstrap";
import {HelpModule} from "../help/help.module";
import {CommonSharedModule} from "../common-shared.module";
import {CommonModule} from "@angular/common";
import {TranslocoModule} from "@jsverse/transloco";
import {BehaviorSubject} from "rxjs";

@Component({
  selector: 'mes-notification-area',
  templateUrl: './notification-area.component.html',
  styles: [
    `
      ::-webkit-scrollbar {
        width: 10px;
      }

      /* Handle */
      ::-webkit-scrollbar-thumb {
        background: #6c757d;
        border-radius: 3px;
      }

      /* Handle on hover */
      ::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      .message-area {
        overflow: hidden;
      }

      .message-area:hover {
        overflow-y: scroll;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    CommonSharedModule,
    HelpModule,
    InlineSVGModule,
    NgbDropdownModule,
    TranslocoModule
  ]
})
export class NotificationAreaComponent extends BaseComponent implements OnInit {
  memoryUsage: number;
  constructor(public changeDetector: ChangeDetectorRef, private mesNotificationService: MesNotificationService) {
    super();
  }

  messages: NotificationMessage[] = [];
  status$ = new BehaviorSubject<'closed' | 'minimized' | 'maximized'>('closed');
  dockMode: 'left' | 'right' = 'right';
  hasUnreadMark: boolean = false;
  pendingTasks: { id: string, text: string }[] = [];
  page: Page;
  isScrolledToBottom: boolean = true;
  messageArea: HTMLElement;
  showMemoryUsage: boolean = false;

  ngOnInit(): void {
    this.configureReceivingMessages();
    this.configureReceivingQueue();
    this.listenOpenRequest();
    this.initializePage();
  }

  initializePage() {
    this.messages = [];
    this.page = new Page({Size: 100, Total: this.messages.length, Pages: 1});
  }

  private configureReceivingMessages() {
    const source$ = this.mesNotificationService.progress$;
    source$.pipe(buffer(source$.pipe(sampleTime(750))), takeUntil(this.componentDestroyed)).subscribe(messages => {
      if (messages.length) {
        this.publish(messages);
      }
    });
  }

  private publish(messages: NotificationMessage[]) {
    this.status$.next(this.status$.value === 'closed' ? 'maximized' : this.status$.value);
    this.hasUnreadMark = this.status$.value === 'minimized' || !this.isScrolledToBottom;
    this.messages.push(...messages);
    this.updatePage();
    this.messages = [...this.messages];
    this.calculateMemoryUsage();
    this.changeDetector.detectChanges();
    this.scrollToLastMessage();
  }

  updatePage() {
    this.page.Total = this.messages.length;
    this.page.Pages = Math.ceil(this.page.Total / this.page.Size);
    this.page.Number = this.page.Pages - 1;
  }

  private configureReceivingQueue() {
    this.mesNotificationService.queue$.pipe(takeUntil(this.componentDestroyed)).subscribe(queue => {
      this.pendingTasks = queue;
    });
  }

  private listenOpenRequest() {
    this.mesNotificationService.openNotifications$.pipe(filter(value => value === true), takeUntil(this.componentDestroyed)).subscribe(() => {
      this.status$.next('maximized');
      this.changeDetector.detectChanges();
      this.scrollToLastMessage();
    });
  }

  scrollToLastMessage() {
    if (this.isScrolledToBottom) {
      setTimeout(() => {
        this.messageArea ??= document.getElementById('message-area');
        const lastMsg = this.messageArea?.lastElementChild;
        if (lastMsg) {
          lastMsg.scrollIntoView({behavior: 'smooth'});
        }
      }, 100);
    }
  }

  onScroll(event) {
    const tracker = event.target;
    const limit = tracker.scrollHeight - tracker.clientHeight;
    const tolerance = 80;
    this.isScrolledToBottom = Math.abs(limit - event.target.scrollTop) < tolerance;
    if (this.isScrolledToBottom && this.status$.value === 'maximized') {
      this.hasUnreadMark = false;
    }
  }

  toggleStatus() {
    this.status$.next(this.status$.value === 'minimized' ? 'maximized' : 'minimized');
    this.hasUnreadMark = false;
    this.changeDetector.detectChanges();
    this.scrollToLastMessage();
  }

  cancel(taskId: string) {
    this.mesNotificationService.cancelBackgroundTask(taskId).pipe(takeUntil(this.componentDestroyed)).subscribe(canceled => {
      if (canceled) {
        this.messages.forEach(message => {
          if (message.taskId === taskId) {
            message.isCanceled = true;
          }
        });
        this.changeDetector.detectChanges();
      }
    });
  }

  clearPending() {
    this.mesNotificationService.clearPendingTasks().pipe(takeUntil(this.componentDestroyed)).subscribe(canceledIds => {
      const message = new NotificationMessage({task: 'Clear pending tasks', timestamp: new Date()});
      message.info = canceledIds.length > 0 ? canceledIds.length + ' task(s) cleared from list. Pending tasks list is empty.' : 'There were no tasks pending'
      this.messages.forEach(message => {
        if (canceledIds.includes(message.taskId)) {
          message.isCanceled = true;
        }
      });
      this.publish([message]);
      this.changeDetector.detectChanges();
    });
  }

  testMessage() {
    this.mesNotificationService.testMessage().pipe(takeUntil(this.componentDestroyed)).subscribe();
  }

  showPrevious() {
    this.page.Number--;
    this.messages = [...this.messages];
  }

  showNext() {
    this.page.Number++;
    this.messages = [...this.messages];
  }

  showAll() {
    this.page.Number = 0;
    this.page.Pages = 1;
    this.page.Size = Number.MAX_SAFE_INTEGER;
    this.messages = [...this.messages];
  }

  trackByTimeStamp(index: number, item: NotificationMessage) {
    return item.timestamp;
  }

  private calculateMemoryUsage() {
    if(this.messages.length > 0){
      let notificationMessage = this.messages[this.messages.length-1];
      this.memoryUsage = notificationMessage.memory?.mb / notificationMessage.memory?.total * 100;
    }
  }
}
